<template>
	<div>
		<Card>
			
			<Form ref="searchForm" :model="searchForm" inline :label-width="80">
				<FormItem :label="$t('SplitRule.Station')">
					<Select v-model="searchForm.stationId" :placeholder="$t('SplitRule.SelectStation')" style="width: 200px" clearable>
						<Option v-for="item in stationList" :key="item.StationId" :value="item.StationId">
							{{ item.StationName || '-' }}
						</Option>
					</Select>
				</FormItem>
				<FormItem :label="$t('SplitRule.Status')">
					<Select v-model="searchForm.isEnable" :placeholder="$t('SplitRule.SelectStatus')" style="width: 120px" clearable>
						<Option :value="1">{{ $t('SplitRule.Enabled') }}</Option>
						<Option :value="0">{{ $t('SplitRule.Disabled') }}</Option>
					</Select>
				</FormItem>
				<FormItem>
					<Button type="primary" @click="handleSearch">{{ $t('Common.Search') }}</Button>
					<Button type="default" @click="handleReset" style="margin-left: 8px;">{{ $t('Common.Reset') }}</Button>
				</FormItem>
				<FormItem style="float: right;">
					<Button type="success" @click="handleAdd" icon="md-add" v-if="hasWidgets('add')">{{ $t('SplitRule.Add') }}</Button>
				</FormItem>
			</Form>
		</Card>

		<Card>
			<Table border :columns="columns" :data="tableData" :loading="tableLoading">
				<template #isEnable="{ row }">
					<Tag :color="row.IsEnable === 1 ? 'success' : 'default'">
						{{ row.IsEnable === 1 ? $t('SplitRule.Enabled') : $t('SplitRule.Disabled') }}
					</Tag>
				</template>
				<template #platformRate="{ row }">
					{{ (row.PlatformRate * 100).toFixed(2) }}%
				</template>
				<template #stationRate="{ row }">
					{{ (row.StationRate * 100).toFixed(2) }}%
				</template>
				<template #createTime="{ row }">
					{{ formatDateTime(row.CreateTime) }}
				</template>
				<template #action="{ row }">
					<Button type="primary" size="small" @click="handleEdit(row)" style="margin-right: 5px;">{{ $t('SplitRule.Edit') }}</Button>
					<Button type="warning" size="small" @click="handleToggleStatus(row)">{{ row.IsEnable === 1 ? $t('SplitRule.Disabled') : $t('SplitRule.Enabled') }}</Button>
				</template>
			</Table>
			<div style="margin-top: 16px; text-align: right;">
				<Page
					:total="pagination.total"
					:current="pagination.current"
					:page-size="pagination.pageSize"
					:page-size-opts="[10, 20, 50, 100]"
					show-total
					show-sizer
					@on-change="handlePageChange"
					@on-page-size-change="handlePageSizeChange"
				/>
			</div>
		</Card>

		<!-- 新增/编辑弹窗 -->
		<Modal
			v-model="modalVisible"
			:title="modalTitle"
			:mask-closable="false"
			@on-cancel="handleModalCancel"
		>
			<Form ref="ruleForm" :model="formData" :rules="formRules" :label-width="120">
				<FormItem :label="$t('SplitRule.Station')" prop="stationId">
					<Select v-model="formData.stationId" :placeholder="$t('SplitRule.SelectStation')" filterable :disabled="isEdit">
						<Option v-for="item in stationList" :key="item.StationId" :value="item.StationId">
							{{ item.StationName || '-' }}
						</Option>
					</Select>
				</FormItem>
				<FormItem :label="$t('SplitRule.PlatformRate')" prop="platformRate">
					<InputNumber
						v-model="formData.platformRate"
						:min="0"
						:max="1"
						:step="0.0001"
						:precision="4"
						style="width: 200px;"
						:placeholder="$t('SplitRule.PlatformRatePlaceholder')"
						@on-change="handleRateChange"
					/>
					<span style="margin-left: 10px; color: #999;">{{ $t('SplitRule.PlatformRateHint') }}</span>
				</FormItem>
				<FormItem :label="$t('SplitRule.StationRate')" prop="stationRate">
					<InputNumber
						v-model="formData.stationRate"
						:min="0"
						:max="1"
						:step="0.0001"
						:precision="4"
						style="width: 200px;"
						:placeholder="$t('SplitRule.StationRatePlaceholder')"
						@on-change="handleRateChange"
					/>
					<span style="margin-left: 10px; color: #999;">{{ $t('SplitRule.StationRateHint') }}</span>
				</FormItem>
				<FormItem :label="$t('SplitRule.TotalRate')">
					<span :style="{ color: totalRate <= 1 ? '#52c41a' : '#ed4014', fontWeight: 'bold' }">
						{{ (totalRate * 100).toFixed(2) }}%
					</span>
					<span v-if="totalRate > 1" style="margin-left: 10px; color: #ed4014;">{{ $t('SplitRule.TotalRateExceed') }}</span>
				</FormItem>
				<FormItem :label="$t('SplitRule.Status')" prop="isEnable">
					<RadioGroup v-model="formData.isEnable">
						<Radio :label="1">{{ $t('SplitRule.Enabled') }}</Radio>
						<Radio :label="0">{{ $t('SplitRule.Disabled') }}</Radio>
					</RadioGroup>
				</FormItem>
			</Form>
			<div slot="footer">
				<Button @click="handleModalCancel">{{ $t('Common.Cancel') }}</Button>
				<Button type="primary" @click="handleModalOk" :loading="modalLoading">{{ $t('Common.Confirm') }}</Button>
			</div>
		</Modal>
	</div>
</template>

<script>
	import { mapState } from 'vuex';
	import webapi from '../../../webapi';

	export default {
		name: 'SplitRule',
			data() {
			const validateRateSum = (_rule, _value, callback) => {
				const platformRate = this.formData.platformRate || 0;
				const stationRate = this.formData.stationRate || 0;
				if (platformRate + stationRate > 1) {
					callback(new Error(this.$t('SplitRule.RateSumExceed')));
				} else {
					callback();
				}
			};

			const validateRate = (_rule, value, callback) => {
				if (value === null || value === undefined || value === '') {
					callback(new Error(this.$t('SplitRule.RateRequired')));
				} else if (value < 0 || value > 1) {
					callback(new Error(this.$t('SplitRule.RateRangeError')));
				} else {
					callback();
				}
			};

			const validateStation = (_rule, value, callback) => {
				if (value === null || value === undefined || value === '') {
					callback(new Error(this.$t('SplitRule.StationRequired')));
				} else {
					callback();
				}
			};


			return {
				searchForm: {
					stationId: undefined,
					isEnable: undefined
				},
				tableData: [],
				tableLoading: false,
				pagination: {
					current: 1,
					pageSize: 10,
					total: 0
				},
				modalVisible: false,
				modalTitle: '',
				modalLoading: false,
				isEdit: false,
				currentId: null,
				formData: {
					stationId: undefined,
					platformRate: null,
					stationRate: null,
					isEnable: 1
				},
				formRules: {
					stationId: [
						{ required: true, validator: validateStation, trigger: 'change' }
					],
					platformRate: [
						{ required: true, validator: validateRate, trigger: 'blur' },
						{ validator: validateRateSum, trigger: 'blur' }
					],
					stationRate: [
						{ required: true, validator: validateRate, trigger: 'blur' },
						{ validator: validateRateSum, trigger: 'blur' }
					]
				},
				stationList: []
			};
		},
		computed: {
			...mapState({
				Stations: 'Stations'
			}),
			totalRate() {
				const platformRate = this.formData.platformRate || 0;
				const stationRate = this.formData.stationRate || 0;
				return platformRate + stationRate;
			},
			columns() {
				return [
					{
						title: this.$t('SplitRule.Index'),
						type: 'index',
						width: 80,
						align: 'center'
					},
					{
						title: this.$t('SplitRule.Operator'),
						key: 'OperatorName',
						minWidth: 150
					},
					{
						title: this.$t('SplitRule.StationName'),
						key: 'StationName',
						minWidth: 200
					},
					{
						title: this.$t('SplitRule.PlatformRate'),
						slot: 'platformRate',
						width: 150,
						align: 'center'
					},
					{
						title: this.$t('SplitRule.StationRate'),
						slot: 'stationRate',
						width: 150,
						align: 'center'
					},
					{
						title: this.$t('SplitRule.Status'),
						slot: 'isEnable',
						width: 100,
						align: 'center'
					},
					{
						title: this.$t('SplitRule.CreateTime'),
						slot: 'createTime',
						width: 180,
						align: 'center'
					},
					{
						title: this.$t('SplitRule.Operation'),
						slot: 'action',
						width: 200,
						align: 'center',
						fixed: 'right'
					}
				];
			}
		},
		created() {
			this.modalTitle = this.$t('SplitRule.AddSplitRule');
			this.getStationList();
			this.getSplitRuleList();
			this.testTranslations();
		},
		methods: {
			// 权限检查方法
			hasWidgets(widgetName) {
				return this.$route.meta.widgets && this.$route.meta.widgets.find(w => w.Name === widgetName);
			},
			
			// 测试翻译
			testTranslations() {
				console.log('===== 测试翻译 =====');
				console.log('$i18n:', this.$i18n);
				console.log('$i18n.locale:', this.$i18n ? this.$i18n.locale : 'undefined');
				console.log('当前语言:', this.$i18n ? this.$i18n.locale : 'undefined');

				if (this.$i18n && this.$i18n.messages) {
					const messages = this.$i18n.messages[this.$i18n.locale];
					console.log('所有消息键:', Object.keys(messages));
					console.log('SplitRule:', messages.SplitRule);
					console.log('StationManagement:', messages.StationManagement);
					console.log('SplitManagement (Menu):', messages.SplitManagement);
				}

				console.log('SplitRule.Operator:', this.$t('SplitRule.Operator'));
				console.log('SplitRule.StationName:', this.$t('SplitRule.StationName'));
				console.log('SplitRule.PlatformRate:', this.$t('SplitRule.PlatformRate'));
				console.log('StationManagement.StationName:', this.$t('StationManagement.StationName'));
			},

			// 获取场站列表
			async getStationList() {
				try {
					const res = await this.$axios.post(webapi.base_api_url + '/Home/GetCurrentUserStations', {});
					if (res.status === 200 && res.data.Code === 0) {
						this.stationList = res.data.Data.List || [];
						console.log('场站列表数据:', this.stationList);
					}
				} catch (error) {
					console.error('获取场站列表失败:', error);
					this.$Message.error(this.$t('SplitRule.GetStationListFail'));
				}
			},

			// 获取分账规则列表
			async getSplitRuleList() {
				this.tableLoading = true;
				try {
					const params = {
						stationId: this.searchForm.stationId,
						isEnable: this.searchForm.isEnable,
						pageIndex: this.pagination.current,
						pageSize: this.pagination.pageSize
					};

					const res = await this.$axios.post(webapi.base_api_url + '/Split/GetSplitRules', params);

					if (res.status === 200 && res.data.Code === 0) {
						this.tableData = res.data.Data.List || [];
						this.pagination.total = res.data.Total || 0;
					} else {
						this.$Message.error(res.data.Message || this.$t('SplitRule.GetSplitRuleListFail'));
					}
				} catch (error) {
					console.error('获取分账规则列表失败:', error);
					this.$Message.error(this.$t('SplitRule.GetSplitRuleListFail'));
				} finally {
					this.tableLoading = false;
				}
			},

			// 搜索
			handleSearch() {
				this.pagination.current = 1;
				this.getSplitRuleList();
			},

			// 运营商选择变化
			handleOperatorChange(value) {
				console.log('运营商选择变化:', value);
				this.searchForm.operatorId = value;
			},

			// 重置
			handleReset() {
				this.searchForm = {
					stationId: undefined,
					isEnable: undefined
				};
				this.pagination.current = 1;
				this.getSplitRuleList();
			},

			// 新增
			handleAdd() {
				this.isEdit = false;
				this.modalTitle = this.$t('SplitRule.AddSplitRule');
				this.currentId = null;
				this.modalVisible = true;
				this.$nextTick(() => {
					this.$refs.ruleForm.resetFields();
					// 重置后不立即设置 formData，让 resetFields 保留初始值
				});
			},

			// 编辑
			handleEdit(row) {
				this.isEdit = true;
				this.modalTitle = this.$t('SplitRule.EditSplitRule');
				this.currentId = row.Id;
				this.formData = {
					stationId: row.StationId,
					platformRate: row.PlatformRate,
					stationRate: row.StationRate,
					isEnable: row.IsEnable
				};
				this.modalVisible = true;
			},

			// 切换状态
			handleToggleStatus(row) {
				const newStatus = row.IsEnable === 1 ? 0 : 1;
				const actionText = newStatus === 1 ? this.$t('SplitRule.Enabled') : this.$t('SplitRule.Disabled');
				const title = newStatus === 1 ? this.$t('SplitRule.ConfirmEnable') : this.$t('SplitRule.ConfirmDisable');
				const contentStart = newStatus === 1 ? this.$t('SplitRule.ConfirmEnableContent') : this.$t('SplitRule.ConfirmDisableContent');
				const contentEnd = this.$t('SplitRule.ConfirmEnableEnd');
				const successText = `${actionText}${this.$t('SplitRule.EnableSuccess')}`;

				this.$Modal.confirm({
					title: title,
					content: `${contentStart}"${row.StationName}"${contentEnd}`,
					onOk: async () => {
						try {
							const res = await this.$axios.post(webapi.base_api_url + '/Split/UpdateSplitRuleStatus', {
								id: row.Id,
								isEnable: newStatus
							});

							if (res.status === 200 && res.data.Code === 0) {
								this.$Message.success(successText);
								this.getSplitRuleList();
							} else {
								this.$Message.error(res.data.Message || this.$t('SplitRule.OperationFail'));
							}
						} catch (error) {
							console.error('操作失败:', error);
							this.$Message.error(this.$t('SplitRule.OperationFail'));
						}
					}
				});
			},

			// 比例变化
			handleRateChange() {
				this.$refs.ruleForm.validateField('platformRate');
				this.$refs.ruleForm.validateField('stationRate');
			},

			// 弹窗确定
			handleModalOk() {
				this.$refs.ruleForm.validate(async (valid) => {
				if (valid) {
					if (this.totalRate > 1) {
						this.$Message.error(this.$t('SplitRule.RateSumExceed'));
						return;
					}

					this.modalLoading = true;
					try {
						const apiUrl = webapi.base_api_url + '/Split/AddSplitRule';

						const params = { ...this.formData };
						if (this.isEdit) {
							params.id = this.currentId;
						}

						console.log('提交参数:', params);
						const res = await this.$axios.post(apiUrl, params);

						if (res.status === 200 && res.data.Code === 0) {
							this.$Message.success(this.isEdit ? this.$t('SplitRule.UpdateSuccess') : this.$t('SplitRule.AddSuccess'));
							this.modalVisible = false;
							this.getSplitRuleList();
						} else {
							this.$Message.error(res.data.Message || (this.isEdit ? this.$t('SplitRule.UpdateFail') : this.$t('SplitRule.AddFail')));
						}
					} catch (error) {
						console.error('操作失败:', error);
						this.$Message.error(this.isEdit ? this.$t('SplitRule.UpdateFail') : this.$t('SplitRule.AddFail'));
					} finally {
						this.modalLoading = false;
					}
				}
				});
			},

			// 弹窗取消
			handleModalCancel() {
				this.modalVisible = false;
				this.$refs.ruleForm && this.$refs.ruleForm.resetFields();
			},

			// 分页变化
			handlePageChange(page) {
				this.pagination.current = page;
				this.getSplitRuleList();
			},

			// 每页条数变化
			handlePageSizeChange(pageSize) {
				this.pagination.pageSize = pageSize;
				this.pagination.current = 1;
				this.getSplitRuleList();
			},

			// 格式化日期时间
			formatDateTime(dateTime) {
				if (!dateTime) return '-';
				const date = new Date(dateTime);
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, '0');
				const day = String(date.getDate()).padStart(2, '0');
				const hours = String(date.getHours()).padStart(2, '0');
				const minutes = String(date.getMinutes()).padStart(2, '0');
				const seconds = String(date.getSeconds()).padStart(2, '0');
				return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
			}
		}
	};
</script>

<style scoped>
	/deep/.ivu-form-item {
		margin-bottom: 16px;
	}
</style>